'use client';
import React, { useState } from 'react';

interface Destination {
  name: string;
  subtitle: string;
  image: string;
  alt: string;
  description: string;
  highlights: string[];
  tag: string;
  jewishHeritage?: boolean;
}

const destinations: Destination[] = [
{
  name: 'Casablanca',
  subtitle: 'The White City',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1686a4766-1776644732132.png",
  alt: 'Hassan II Mosque in Casablanca Morocco with ocean backdrop and towering minaret',
  description: 'Morocco\'s cosmopolitan hub, home to the magnificent Hassan II Mosque, Art Deco architecture, and a vibrant Jewish heritage in the historic Mellah.',
  highlights: ['Hassan II Mosque', 'Mellah Quarter', 'Art Deco District', 'Corniche'],
  tag: 'Most Popular',
  jewishHeritage: true
},
{
  name: 'Marrakech',
  subtitle: 'The Red City',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cac2fd04-1765111002310.png",
  alt: 'Marrakech Djemaa el-Fna square with traditional Moroccan architecture and souks at dusk',
  description: 'A sensory feast of vibrant souks, stunning palaces, and ancient medinas. Marrakech captivates with its timeless beauty and cultural richness.',
  highlights: ['Djemaa el-Fna', 'Bahia Palace', 'Majorelle Garden', 'Medina Souks'],
  tag: 'Must Visit',
  jewishHeritage: true
},
{
  name: 'Fes',
  subtitle: 'The Spiritual Capital',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1454c12d6-1772529852868.png',
  alt: 'Fes el-Bali ancient medina with traditional tanneries and historic Jewish quarter',
  description: 'The world\'s oldest living medieval city. Fes is home to one of Morocco\'s most significant Jewish communities and a UNESCO World Heritage medina.',
  highlights: ['Fes el-Bali', 'Jewish Mellah', 'Chouara Tannery', 'Al-Qarawiyyin'],
  tag: 'Jewish Heritage',
  jewishHeritage: true
},
{
  name: 'Rabat',
  subtitle: 'The Royal Capital',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d389b527-1774492338711.png",
  alt: 'Rabat Hassan Tower and Mausoleum of Mohammed V in Morocco\'s elegant royal capital city',
  description: 'Morocco\'s elegant royal capital blends ancient history with modern sophistication. Explore the Kasbah des Oudaias and the magnificent Hassan Tower.',
  highlights: ['Hassan Tower', 'Kasbah des Oudaias', 'Royal Palace', 'Chellah Ruins'],
  tag: 'Royal City'
},
{
  name: 'Essaouira',
  subtitle: 'The Windy City',
  image: "https://images.unsplash.com/photo-1727251982109-db11828ec0ac",
  alt: 'Essaouira blue and white medina walls with Atlantic Ocean views and fishing harbor',
  description: 'A UNESCO-listed coastal gem with a rich Jewish heritage. Essaouira\'s blue and white medina, ancient ramparts, and Atlantic breezes create an unforgettable atmosphere.',
  highlights: ['Historic Ramparts', 'Jewish Mellah', 'Medina UNESCO', 'Atlantic Beach'],
  tag: 'Jewish Heritage',
  jewishHeritage: true
},
{
  name: 'Chefchaouen',
  subtitle: 'The Blue Pearl',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1299740e0-1784231440278.png',
  alt: 'Chefchaouen blue painted streets and buildings in the Rif Mountains of northern Morocco',
  description: 'Nestled in the Rif Mountains, this enchanting blue city is one of Morocco\'s most photographed destinations. A magical escape of cobalt streets and mountain vistas.',
  highlights: ['Blue Medina', 'Rif Mountains', 'Ras El Maa', 'Plaza Uta el-Hammam'],
  tag: 'Scenic Beauty'
},
{
  name: 'Tangier',
  subtitle: 'Gateway to Africa',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11952e5b9-1785070342291.png',
  alt: 'Tangier port city with Mediterranean and Atlantic views in northern Morocco',
  description: 'Where Europe meets Africa. Tangier\'s cosmopolitan history, vibrant medina, and stunning views across the Strait of Gibraltar make it a fascinating destination.',
  highlights: ['Kasbah Museum', 'Cap Spartel', 'Hercules Caves', 'Grand Socco'],
  tag: 'Gateway City'
},
{
  name: 'Sahara Desert',
  subtitle: 'The Golden Dunes',
  image: "https://images.unsplash.com/photo-1636459932653-74f87c406e57",
  alt: 'Sahara Desert golden sand dunes at sunset with camel caravan silhouettes in Morocco',
  description: 'Experience the magic of the Sahara — endless golden dunes, camel treks at sunrise, and nights under a canopy of stars at a luxury desert camp.',
  highlights: ['Erg Chebbi Dunes', 'Camel Trekking', 'Desert Camps', 'Stargazing'],
  tag: 'Adventure'
},
{
  name: 'Meknes',
  subtitle: 'The Ismaili Capital',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f487a9a3-1776992986816.png',
  alt: 'Meknes Bab Mansour gate and historic imperial city walls in Morocco',
  description: 'One of Morocco\'s four imperial cities, Meknes boasts magnificent gates, ancient granaries, and a historic Jewish mellah that tells centuries of coexistence.',
  highlights: ['Bab Mansour', 'Jewish Mellah', 'Moulay Ismail Mausoleum', 'Volubilis nearby'],
  tag: 'Imperial City',
  jewishHeritage: true
},
{
  name: 'Atlas Mountains',
  subtitle: 'Roof of Morocco',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18627cbdd-1785077201906.png",
  alt: 'High Atlas Mountains with snow-capped peaks and traditional Berber villages in Morocco',
  description: 'Majestic mountain landscapes, traditional Berber villages, and breathtaking valleys. The Atlas Mountains offer an authentic glimpse into Morocco\'s rural soul.',
  highlights: ['Toubkal Peak', 'Berber Villages', 'Ourika Valley', 'Ait Benhaddou'],
  tag: 'Nature'
},
{
  name: 'Volubilis',
  subtitle: 'Ancient Roman City',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c64be429-1785070341809.png',
  alt: 'Volubilis ancient Roman ruins with triumphal arch and mosaic floors in Morocco',
  description: 'Morocco\'s best-preserved Roman ruins, a UNESCO World Heritage Site. Walk among ancient temples, triumphal arches, and stunning mosaic floors dating to the 3rd century.',
  highlights: ['Roman Ruins', 'UNESCO Heritage', 'Ancient Mosaics', 'Triumphal Arch'],
  tag: 'Ancient History'
},
{
  name: 'Jewish Mellahs',
  subtitle: 'Living History',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16fa9b670-1785077201913.png",
  alt: 'Historic Jewish Mellah quarter with traditional architecture in a Moroccan city',
  description: 'Journey through Morocco\'s historic Jewish quarters — the mellahs of Fes, Marrakech, Casablanca, and Essaouira — where centuries of Jewish life left an indelible mark.',
  highlights: ['Fes Mellah', 'Marrakech Mellah', 'Casablanca Mellah', 'Essaouira Mellah'],
  tag: 'Jewish Heritage',
  jewishHeritage: true
}];


const filters = ['All', 'Jewish Heritage', 'Imperial City', 'Nature', 'Most Popular', 'Adventure'];

export default function Destinations() {
  const [activeFilter, setActiveFilter] = useState('All');

  const filtered =
  activeFilter === 'All' ?
  destinations :
  destinations.filter((d) => d.tag === activeFilter);

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="destinations" className="section-padding bg-navy">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12 animate-on-scroll">
          <span className="tag-gold">Destinations</span>
          <h2 className="section-title mt-4 mb-4 text-white">
            Discover Morocco&apos;s
            <br />
            <span className="text-gold italic">Most Magnificent Places</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4 text-white/70">
            From imperial cities to desert dunes, coastal gems to mountain villages — explore
            Morocco&apos;s most extraordinary destinations with your private guide.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll">
          {filters.map((filter) =>
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`font-sans text-xs font-bold uppercase tracking-wider px-5 py-2.5 border transition-all duration-300 ${
            activeFilter === filter ?
            'bg-gold text-navy-dark border-gold' : 'bg-transparent text-white/70 border-white/30 hover:border-gold hover:text-gold'}`
            }>
            
              {filter}
            </button>
          )}
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((dest, index) =>
          <div
            key={dest.name}
            className="destination-card animate-on-scroll"
            style={{ transitionDelay: `${index % 4 * 80}ms` }}
            onClick={() => handleNavClick('#contact')}>
            
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                src={dest.image}
                alt={dest.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy" />
              
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />

                {/* Tag */}
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="font-sans text-xs font-bold uppercase tracking-wider px-3 py-1 bg-gold text-navy-dark">
                    {dest.tag}
                  </span>
                  {dest.jewishHeritage &&
                <span className="font-sans text-xs font-bold px-2 py-1 bg-navy/80 text-gold border border-gold/40">
                      ✡️
                    </span>
                }
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-sans text-xs text-gold uppercase tracking-widest mb-1">
                    {dest.subtitle}
                  </p>
                  <h3 className="font-serif text-xl font-bold text-white mb-2">{dest.name}</h3>

                  {/* Highlights - visible on hover */}
                  <div className="max-h-0 overflow-hidden group-hover:max-h-40 transition-all duration-500">
                    <p className="font-sans text-xs text-white/80 leading-relaxed mb-3">
                      {dest.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {dest.highlights.map((h) =>
                    <span key={h} className="font-sans text-xs text-gold/90 bg-navy/60 px-2 py-0.5">
                          {h}
                        </span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-14 animate-on-scroll">
          <p className="font-sans text-white/70 mb-6">
            Want to visit multiple destinations? We&apos;ll create the perfect multi-city itinerary for you.
          </p>
          <button
            onClick={() => handleNavClick('#contact')}
            className="btn-gold">
            
            Plan My Morocco Journey
          </button>
        </div>
      </div>
    </section>);

}